import {
	DropDownList,
	DropDownListChangeEvent,
	DropDownListFilterChangeEvent,
	DropDownListProps,
	ComboBox,
	ComboBoxChangeEvent,
	ComboBoxFilterChangeEvent,
	ComboBoxProps,
} from "@progress/kendo-react-dropdowns";
import React, { useRef, useState } from "react";
import {
	CompositeFilterDescriptor,
	FilterDescriptor,
	filterBy,
} from "@progress/kendo-data-query";
import { COLOR } from "../utils/constant";
import { useInfoStore } from "store/error";

export interface IDropDownListItem extends DropDownListProps {
	//when passing any[] then  you have define the  textField and dataItemKey
	data: any[];
	//defaultItem should match the values of textField and dataItemKey use
	defaultItem?: any;
	filterable?: boolean;
	filterDelay?: number;
	textField?: string;
	dataItemKey?: string;
	disabled?: boolean;
	label?: string;
	placeholder?: string;
	serverFilter?: boolean;
	handleFilterChange?: (e: DropDownListFilterChangeEvent) => void;

	showError?: boolean;
	borderColor?: string;
	onSelect?: (data: any) => void;
	serverFilterApi?: (text: string) => Promise<any[]>;
	serverApi?: () => Promise<any[]>;
	// pass a function to get data  from api
	getData?: () => void;
}

const CustomDropDownList: React.FC<IDropDownListItem> = ({
	data = [],
	defaultItem,
	serverFilter = true,
	filterable = false,
	value,
	filterDelay = 1000,
	defaultValue,
	getData,
	dataItemKey = "value",
	textField = "text",
	disabled,
	onChange,
	showError = false,
	serverApi,
	...props
}) => {
	const [filterItem, setFilterItem] = useState<{
		data: any[] | null;
		loading: boolean;
	}>({ data: null, loading: false });

	const [navigatingValue, setNavigatingValue] = useState<any>(null);
	// Ref mirror so onClose closure always sees the latest value without stale captures
	const navigatingValueRef = useRef<any>(null);
	const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isArrowNavigating = useRef(false);
	// True when Enter was pressed while arrow-navigating � commit happens in onClose
	const isConfirming = useRef(false);

	const { setError } = useInfoStore();

	const handleKeyDownCapture = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowUp" || e.key === "ArrowDown") {
			isArrowNavigating.current = true;
			isConfirming.current = false;
		} else if (e.key === "Enter" && navigatingValueRef.current !== null) {
			// User pressed Enter after arrow-navigating � defer commit to onClose
			isArrowNavigating.current = false;
			isConfirming.current = true;
		} else {
			isArrowNavigating.current = false;
			isConfirming.current = false;
		}
	};

	const handleChange = (e: DropDownListChangeEvent) => {
		if (isArrowNavigating.current) {
			// Update local highlight so Kendo scrolls the list � do not commit to parent
			navigatingValueRef.current = e.target.value;
			setNavigatingValue(e.target.value);
			return;
		}
		if (isConfirming.current) {
			// Enter after navigation: Kendo may or may not fire onChange here.
			// Either way, onClose will do the commit � skip here to avoid double commit.
			return;
		}
		// Click selection � commit directly
		navigatingValueRef.current = null;
		setNavigatingValue(null);
		if (onChange) onChange(e);
		if (props.onSelect) props.onSelect(e.target.value);
	};

	const handleClose = (e?: any) => {
		if (isConfirming.current && navigatingValueRef.current !== null) {
			// Commit the arrow-navigated item on Enter
			if (props.onSelect) props.onSelect(navigatingValueRef.current);
		}
		// Reset all navigation state (covers Escape / click-outside too)
		navigatingValueRef.current = null;
		setNavigatingValue(null);
		isArrowNavigating.current = false;
		isConfirming.current = false;
	};

	const handleGetData = () => {
		// request data from api
		if (serverApi) {
			serverApi()
				.then((data) => {
					setFilterItem({ loading: false, data: data || [] });
				})
				.catch((err) => {
					if (err?.response?.data) {
						if (err?.response?.data.returnText) {
							setError(err?.response?.data.returnText);
						} else {
							setError(err?.response?.data);
						}
					} else {
						setError(err.message);
					}
				});
		}
		if (filterable) return;
		if (data.length < 1 && getData) {
			getData();
		}
	};

	const filterData = (filter: FilterDescriptor | CompositeFilterDescriptor) => {
		if (!data) {
			return filterBy([], filter);
		}

		return filterBy(data, filter);
	};

	const filterChange = (event: DropDownListFilterChangeEvent) => {
		if (props.handleFilterChange) {
			props.handleFilterChange(event);
			return;
		}

		if (filterDebounceRef.current) {
			clearTimeout(filterDebounceRef.current);
		}

		setFilterItem({ ...filterItem, loading: true });

		const filterValue = event.filter.value;

		filterDebounceRef.current = setTimeout(() => {
			if (serverFilter) {
				if (props.serverFilterApi) {
					props
						.serverFilterApi(filterValue)
						.then((data) => {
							setFilterItem({ loading: false, data });
						})
						.catch((err) => {
							if (err?.response?.data) {
								if (err?.response?.data.returnText) {
									setError(err?.response?.data.returnText);
								} else {
									setError(err?.response?.data);
								}
							} else {
								setError(err.message);
							}
							setFilterItem({ loading: false, data: null });
						});
				}
			} else {
				setFilterItem({ loading: false, data: filterData(event.filter) });
			}
		}, filterDelay);
	};
	let bColor = "";
	if (value && Object.keys(value).length > 0) {
		bColor = COLOR.Green;
	}
	if (props.borderColor) {
		bColor = props.borderColor;
	}
	return (
		// <div
		// 	style={{
		// 		width: "100%",
		// 		display: "flex",
		// 		flexDirection: "column",
		// 		gap: 0,
		// 	}}
		// >
		// 	<Label className={"custom-label"}>{props.label}</Label>
		<div
			onKeyDownCapture={handleKeyDownCapture}
			onMouseDown={() => {
				isArrowNavigating.current = false;
				isConfirming.current = false;
			}}
			style={{ width: "100%" }}
		>
			<DropDownList
				disabled={disabled}
				// itemRender={itemRender}
				itemRender={props.itemRender}
				data={filterItem?.data ? filterItem.data : data}
				textField={textField}
				dataItemKey={dataItemKey}
				value={navigatingValue ?? value}
				defaultItem={defaultItem}
				defaultValue={defaultValue}
				onChange={handleChange}
				filterable={filterable}
				onOpen={handleGetData}
				loading={filterItem.loading}
				onFilterChange={filterChange}
				className={props.className}
				popupSettings={{ ...props.popupSettings }}
				label={props.placeholder || props.label}
				{...props}
				onClose={(e) => {
					handleClose(e);
					if (props.onClose) props.onClose(e);
				}}
				style={{
					backgroundColor: COLOR.Grey98,
					borderColor: showError ? "red" : bColor,
					// borderWidth: "1px",
					width: "100%",
					...props.style,
				}}
			/>
		</div>
		// </div>
	);
};
export default CustomDropDownList;

export interface ComboBoxDropdownProps extends ComboBoxProps {
	label?: string;
	showError?: boolean;
}

export const ComboBoxDropdown: React.FC<ComboBoxDropdownProps> = ({
	label,
	showError = false,
	value,
	...props
}) => {
	let bColor = "";
	if (value && Object.keys(value).length > 0) {
		bColor = COLOR.Green;
	}
	// if (props.borderColor) {
	// 	bColor = props.borderColor;
	// }
	//console.log("ComboBoxDropdown value :", value, props.valid);
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				width: "100%",
				gap: 8,
			}}
		>
			{label && (
				<label style={{ fontSize: 14, fontWeight: 500 }}>{label}</label>
			)}
			<ComboBox
				style={{
					width: "100%",
					backgroundColor: COLOR.Grey98,
					borderColor: !props.valid ? "red" : bColor,
				}}
				value={value}
				allowCustom={true}
				{...props}
			/>
		</div>
	);
};
